import {DateTime, Diagram, DiagramType, nominate} from "platformed-client/types";

// Order-to-Cash (O2C) / Revenue Cycle Process Diagram
// Demonstrates sales order through cash receipt with revenue recognition controls
export const orderToCash: Diagram = {
  diagram_id: nominate("DiagramId", "hardcoded-o2c-001"),
  name: "Order-to-Cash (O2C) Process",
  description:
    "Revenue recognition and cash collection with credit management controls from order entry through payment receipt. Illustrates timing of revenue recognition at shipment and segregation between sales, fulfillment, billing, and collections functions.",
  diagram_type: "ProcessFlow" as DiagramType,
  created_at: "2025-09-25T09:15:00Z" as DateTime,
  updated_at: "2025-11-08T16:45:00Z" as DateTime,
  owner: null,
  deleted: null,
  lanes_enabled: false,
  lanes: {},
  generation_state: {type: "NotStarted"},

  nodes: {
    salesOrder: {
      diagram_node_id: nominate("DiagramNodeId", "o2c-sales-order"),
      label: "Sales Order Entry",
      description: "Sales rep creates sales order with pricing and terms",
      icon: "isoflow.document",
      position: [2, 2], // Sales Team swimlane
    },
    creditCheck: {
      diagram_node_id: nominate("DiagramNodeId", "o2c-credit-check"),
      label: "Credit Check",
      description:
        "Credit manager reviews customer credit status and approves\n\nControls\nAR-01 Customer Credit Review",
      icon: "isoflow.block",
      position: [10, 2], // Credit Management swimlane
    },
    orderApproval: {
      diagram_node_id: nominate("DiagramNodeId", "o2c-order-approval"),
      label: "Order Approval",
      description:
        "Sales manager approves pricing, discounts, and terms\n\nControls\nAR-02 Order & Pricing Authorization",
      icon: "isoflow.block",
      position: [18, 2], // Sales Management swimlane
    },
    inventory: {
      diagram_node_id: nominate("DiagramNodeId", "o2c-inventory"),
      label: "Inventory Allocation",
      description: "System reserves inventory for order",
      icon: "isoflow.server",
      position: [18, 7], // Sales Management swimlane (lower)
    },
    picking: {
      diagram_node_id: nominate("DiagramNodeId", "o2c-picking"),
      label: "Order Picking",
      description:
        "Warehouse picks items and verifies quantities\n\nControls\nAR-03 Independent Verification of Shipment",
      icon: "isoflow.user",
      position: [26, 2], // Warehouse/Fulfillment swimlane
    },
    shipping: {
      diagram_node_id: nominate("DiagramNodeId", "o2c-shipping"),
      label: "Pack & Ship",
      description: "Shipping team packs items and arranges carrier pickup",
      icon: "isoflow.server",
      position: [34, 2], // Shipping swimlane
    },
    invoice: {
      diagram_node_id: nominate("DiagramNodeId", "o2c-invoice"),
      label: "Invoice Generation",
      description: "Billing generates invoice based on shipped items\n\nControls\nAR-04 Automated Billing Trigger",
      icon: "isoflow.document",
      position: [42, 2], // Billing swimlane
    },
    revenue: {
      diagram_node_id: nominate("DiagramNodeId", "o2c-revenue"),
      label: "Revenue Recognition",
      description: "Accounting posts revenue to GL based on delivery",
      icon: "fi.storage.015.book",
      position: [42, 8], // Billing swimlane (lower)
    },
    payment: {
      diagram_node_id: nominate("DiagramNodeId", "o2c-payment"),
      label: "Payment Receipt",
      description: "Customer payment received via check, ACH, wire, or card",
      icon: "isoflow.document",
      position: [50, 2], // Treasury/AR swimlane
    },
    cashApp: {
      diagram_node_id: nominate("DiagramNodeId", "o2c-cash-app"),
      label: "Cash Application",
      description: "AR applies payment to open invoices\n\nControls\nAR-05 Cash Application Transaction Integrity",
      icon: "isoflow.document",
      position: [50, 8], // Treasury/AR swimlane (lower)
    },
    collections: {
      diagram_node_id: nominate("DiagramNodeId", "o2c-collections"),
      label: "Collections Follow-up",
      description: "AR monitors aging and follows up on overdue invoices",
      icon: "isoflow.user",
      position: [50, -2], // Treasury/AR swimlane (upper)
    },
    reconciliation: {
      diagram_node_id: nominate("DiagramNodeId", "o2c-reconciliation"),
      label: "AR Reconciliation",
      description: "Reconcile AR subledger to GL and review aging\n\nControls\nAR-06 AR Subledger to GL Reconciliation",
      icon: "fi.storage.015.book",
      position: [58, 2], // Accounting swimlane
    },
  },

  connections: {
    "o2c-1": {
      diagram_connection_id: nominate("DiagramConnectionId", "o2c-conn-1"),
      source_node_id: nominate("DiagramNodeId", "o2c-sales-order"),
      target_node_id: nominate("DiagramNodeId", "o2c-credit-check"),
      label: "Sales Order",
      color: "blue",
      anchors: [],
    },
    "o2c-2": {
      diagram_connection_id: nominate("DiagramConnectionId", "o2c-conn-2"),
      source_node_id: nominate("DiagramNodeId", "o2c-credit-check"),
      target_node_id: nominate("DiagramNodeId", "o2c-order-approval"),
      label: "Credit Approved",
      color: "green",
      anchors: [],
    },
    "o2c-3": {
      diagram_connection_id: nominate("DiagramConnectionId", "o2c-conn-3"),
      source_node_id: nominate("DiagramNodeId", "o2c-order-approval"),
      target_node_id: nominate("DiagramNodeId", "o2c-inventory"),
      label: "Approved Order",
      color: "green",
      anchors: [{position: [18, 4]}, {position: [18, 6]}],
    },
    "o2c-4": {
      diagram_connection_id: nominate("DiagramConnectionId", "o2c-conn-4"),
      source_node_id: nominate("DiagramNodeId", "o2c-inventory"),
      target_node_id: nominate("DiagramNodeId", "o2c-picking"),
      label: "Pick List",
      color: "blue",
      anchors: [],
    },
    "o2c-5": {
      diagram_connection_id: nominate("DiagramConnectionId", "o2c-conn-5"),
      source_node_id: nominate("DiagramNodeId", "o2c-picking"),
      target_node_id: nominate("DiagramNodeId", "o2c-shipping"),
      label: "Picked Items",
      color: "purple",
      anchors: [],
    },
    "o2c-6": {
      diagram_connection_id: nominate("DiagramConnectionId", "o2c-conn-6"),
      source_node_id: nominate("DiagramNodeId", "o2c-shipping"),
      target_node_id: nominate("DiagramNodeId", "o2c-invoice"),
      label: "Shipping Confirmation",
      color: "green",
      anchors: [],
    },
    "o2c-7": {
      diagram_connection_id: nominate("DiagramConnectionId", "o2c-conn-7"),
      source_node_id: nominate("DiagramNodeId", "o2c-invoice"),
      target_node_id: nominate("DiagramNodeId", "o2c-revenue"),
      label: "Invoice",
      color: "blue",
      anchors: [],
    },
    "o2c-8": {
      diagram_connection_id: nominate("DiagramConnectionId", "o2c-conn-8"),
      source_node_id: nominate("DiagramNodeId", "o2c-invoice"),
      target_node_id: nominate("DiagramNodeId", "o2c-payment"),
      label: "Customer Invoice",
      color: "blue",
      anchors: [],
    },
    "o2c-9": {
      diagram_connection_id: nominate("DiagramConnectionId", "o2c-conn-9"),
      source_node_id: nominate("DiagramNodeId", "o2c-payment"),
      target_node_id: nominate("DiagramNodeId", "o2c-cash-app"),
      label: "Payment",
      color: "green",
      anchors: [],
    },
    "o2c-10": {
      diagram_connection_id: nominate("DiagramConnectionId", "o2c-conn-10"),
      source_node_id: nominate("DiagramNodeId", "o2c-invoice"),
      target_node_id: nominate("DiagramNodeId", "o2c-collections"),
      label: "Overdue Invoice",
      color: "red",
      anchors: [],
    },
    "o2c-11": {
      diagram_connection_id: nominate("DiagramConnectionId", "o2c-conn-11"),
      source_node_id: nominate("DiagramNodeId", "o2c-cash-app"),
      target_node_id: nominate("DiagramNodeId", "o2c-reconciliation"),
      label: "Applied Payment",
      color: "purple",
      anchors: [],
    },
  },

  regions: {},
  engagement: {
    engagement_id: nominate("EngagementId", "p2p-engagement-1"),
    name: "",
    third_party: {name: "", third_party_id: nominate("ThirdPartyId", "p2p-third-party-1")},
  },
};
